import { useSyncExternalStore } from 'react';
import { apiJson } from '@lib/apiClient';
import { currentClient, subscribeClient } from '@lib/clients';

/**
 * ROW 354, THE TOP-BAR CAMPAIGN MENU (William 18 Sep 2026: "Care still has just the client name. It
 * is missing the Campaign - make it a priority to add this."). CLIENT first, then CAMPAIGN, and the
 * whole console follows the one selection.
 *
 * THE RULE THAT MAKES IT SAFE: a campaign selection NEVER hides a row that names no campaign. About a
 * third of events carry none and always will (a member's text, email or voicemail, a call Marny
 * dials, a referral, everything before 14 Sep), and a filter that dropped them would drop member-
 * started work out of her queue. `inCampaign` is that rule, in ONE place, for every screen.
 *
 * The list is READ (GET /api/care/campaigns: the platform's envelope, the registry as fallback),
 * never typed here. The choice is remembered per operator per client. Until the list lands, a
 * remembered choice stands; one the loaded list does not name falls back to All and SAYS so.
 */
export const ALL = 'all';

export interface CampaignDef {
  slug: string;
  label: string;
  isDefault: boolean;
}

interface CampaignsResp {
  client?: string;
  campaigns?: CampaignDef[];
  source?: 'platform' | 'registry' | 'none';
  reason?: string;
  defaultMode?: 'all' | 'current';
}

export const CAMPAIGN_MENU_WORDING = {
  all: 'All campaigns',
  unreadable: (why: string) => `The campaign list could not be read (${why}), so only All campaigns is offered.`,
  gone: (slug: string) => `The campaign last chosen here (${slug}) is not running now, so All campaigns is shown.`,
  scoped: (label: string, others: number) =>
    `Showing ${label} and contacts with no campaign.${others ? ` ${others} open under other campaigns.` : ''}`,
  notByCampaign: (screen: string) => `${screen} is not kept apart by campaign yet: it shows the whole client.`,
  showAll: 'Show all campaigns',
} as const;

const KEY = (client: string) => `membercare.campaign.${client}`;

interface State {
  client: string;
  list: CampaignDef[];
  loaded: boolean;
  source: 'platform' | 'registry' | 'none' | '';
  reason: string;
  notice: string;
  selected: string;
}

function stored(client: string): string {
  try {
    const v = localStorage.getItem(KEY(client));
    return v && /^(all|[a-z0-9][a-z0-9_-]{0,63})$/.test(v) ? v : '';
  } catch {
    return '';
  }
}

function remember(client: string, v: string): void {
  try {
    localStorage.setItem(KEY(client), v);
  } catch {
    /* a browser that will not store it still shows the choice for this visit */
  }
}

let state: State = { client: '', list: [], loaded: false, source: '', reason: '', notice: '', selected: ALL };
const listeners = new Set<() => void>();
const emit = (): void => { for (const l of listeners) l(); };

function freshFor(client: string): State {
  return { client, list: [], loaded: false, source: '', reason: '', notice: '', selected: stored(client) || ALL };
}

/** The selection for the client in force: 'all' or a campaign slug. */
export function currentCampaign(): string {
  if (state.client !== currentClient()) state = freshFor(currentClient());
  return state.selected;
}

export function campaignState(): State {
  if (state.client !== currentClient()) state = freshFor(currentClient());
  return state;
}

export function setCurrentCampaign(v: string): void {
  const client = currentClient();
  if (state.client !== client) state = freshFor(client);
  const next = v === ALL || state.list.some((c) => c.slug === v) || !state.loaded ? v : ALL;
  state = { ...state, selected: next, notice: '' };
  remember(client, next);
  emit();
}

export function subscribeCampaign(fn: () => void): () => void {
  listeners.add(fn);
  const offClient = subscribeClient(fn);
  return () => { listeners.delete(fn); offClient(); };
}

export function useCampaign(): string {
  return useSyncExternalStore(subscribeCampaign, currentCampaign, currentCampaign);
}

export function useCampaignState(): State {
  return useSyncExternalStore(subscribeCampaign, campaignState, campaignState);
}

/** The name a person reads for a slug: the loaded list's label, else the slug itself (never hidden). */
export function campaignLabel(slug: string): string {
  if (slug === ALL) return CAMPAIGN_MENU_WORDING.all;
  return state.list.find((c) => c.slug === slug)?.label ?? slug;
}

/**
 * THE ONE RULE. A row passes when All is chosen, when it names NO campaign (never hidden), or when it
 * names the chosen one.
 */
export function inCampaign(rowCampaign: string | null | undefined, selected: string): boolean {
  return selected === ALL || !rowCampaign || rowCampaign === selected;
}

/**
 * Read the list for the client in force and settle the selection against it. Never throws: a failed
 * read leaves All offered and names the failure.
 */
export async function loadCampaignList(fetcher: (client: string) => Promise<CampaignsResp> = (client) =>
  apiJson<CampaignsResp>(`/api/care/campaigns?client=${encodeURIComponent(client)}`)): Promise<void> {
  const client = currentClient();
  let resp: CampaignsResp;
  try {
    resp = await fetcher(client);
  } catch (err) {
    if (client !== currentClient()) return;
    state = { ...campaignState(), loaded: true, list: [], source: 'none', reason: err instanceof Error ? err.message : String(err) };
    emit();
    return;
  }
  if (client !== currentClient()) return; // the operator switched client while this was in flight
  const list = (resp.campaigns ?? []).filter((c) => c && /^[a-z0-9][a-z0-9_-]{0,63}$/.test(c.slug));
  const had = stored(client);
  let selected = had || ALL;
  let notice = '';
  if (had && had !== ALL && !list.some((c) => c.slug === had)) {
    selected = ALL;
    notice = list.length ? CAMPAIGN_MENU_WORDING.gone(had) : '';
  }
  if (!had && resp.defaultMode === 'current') selected = list.find((c) => c.isDefault)?.slug ?? ALL;
  state = { client, list, loaded: true, source: resp.source ?? 'none', reason: resp.reason ?? '', notice, selected };
  emit();
}
